import { useEffect, useRef, useState } from "react";

/**
 * ChitChat splash screen.
 * Dark charcoal bg with subtle Adinkra/diamond pattern at ~8% opacity.
 * Auto-advances after 2 seconds (or 500ms if reduced motion is preferred).
 * Accessible: focus-trapped, labelled, skippable via Enter/Space/Esc.
 */
export function ChitChatSplash({ onDone }: { onDone: () => void }) {
  const skipBtnRef = useRef<HTMLButtonElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    // Move focus to the skip control so screen-reader users can act immediately.
    skipBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    const delay = reducedMotion ? 500 : 2000;
    const t = setTimeout(onDone, delay);
    return () => clearTimeout(t);
  }, [onDone, reducedMotion]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onDone();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);

  return (
    <div
      className="chitchat-theme fixed inset-0 z-[100] flex flex-col items-center justify-center"
      style={{ background: "#1C1917" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-splash-title"
      aria-describedby="cc-splash-tagline"
    >
      {/* Pattern layer */}
      <svg
        aria-hidden="true"
        focusable="false"
        className="absolute inset-0 w-full h-full"
        style={{ opacity: 0.08 }}
      >
        <defs>
          <pattern id="cc-splash-diamonds" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
            <g fill="none" stroke="#FAF6F1" strokeWidth="1">
              <rect x="12" y="12" width="24" height="24" transform="rotate(45 24 24)" />
              <circle cx="24" cy="24" r="3" />
              <path d="M0 24 L8 16 L16 24 L8 32 Z" />
            </g>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#cc-splash-diamonds)" />
      </svg>

      {/* Logo */}
      <div className="relative flex flex-col items-center gap-5">
        <div
          className="h-24 w-24 rounded-3xl flex items-center justify-center shadow-2xl"
          style={{
            background: "linear-gradient(135deg, #C1502E 0%, #D4A017 100%)",
          }}
          role="img"
          aria-label="ChitChat logo"
        >
          <svg width="52" height="52" viewBox="0 0 52 52" fill="none" aria-hidden="true" focusable="false">
            <path
              d="M8 14a6 6 0 0 1 6-6h24a6 6 0 0 1 6 6v18a6 6 0 0 1-6 6H22l-8 6v-6h-0a6 6 0 0 1-6-6V14Z"
              fill="#FFFFFF"
            />
            <g stroke="#C1502E" strokeWidth="1.5" fill="none">
              <circle cx="26" cy="22" r="5" />
              <line x1="26" y1="13" x2="26" y2="16" />
              <line x1="26" y1="28" x2="26" y2="31" />
              <line x1="17" y1="22" x2="20" y2="22" />
              <line x1="32" y1="22" x2="35" y2="22" />
              <line x1="19.5" y1="15.5" x2="21.6" y2="17.6" />
              <line x1="30.4" y1="26.4" x2="32.5" y2="28.5" />
              <line x1="32.5" y1="15.5" x2="30.4" y2="17.6" />
              <line x1="21.6" y1="26.4" x2="19.5" y2="28.5" />
            </g>
          </svg>
        </div>

        <h1 id="cc-splash-title" className="font-display text-4xl font-extrabold tracking-tight text-white">
          Chit<span style={{ color: "#D4A017" }}>Chat</span>
        </h1>
        <p
          id="cc-splash-tagline"
          className="text-[11px] uppercase tracking-[0.25em] text-white/60"
        >
          Connect · Express · Belong
        </p>
      </div>

      {/* Live region announces loading state to assistive tech */}
      <div role="status" aria-live="polite" className="sr-only">
        ChitChat is loading. Press Enter or Escape to continue.
      </div>

      {/* Visible skip control — receives initial focus */}
      <button
        ref={skipBtnRef}
        type="button"
        onClick={onDone}
        className="absolute bottom-8 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1917] rounded px-3 py-2"
      >
        Skip intro
      </button>
    </div>
  );
}
